import {
  getAuthHeaders, API_BASE_URL 
} from '@/lib/api-client';

export interface GenerateRecipeRequest {
  prompt: string;
  recipeType?: 'finished' | 'sub-recipe' | 'component';
  cuisine?: string;
  dietaryRestrictions?: string[];
}

export interface GeneratedIngredient {
  itemName: string;
  quantity: number;
  unit: string;
  prepNotes?: string;
}

export interface GeneratedRecipe {
  name: string;
  description: string;
  type: 'finished' | 'sub-recipe' | 'component';
  category: string;
  cuisine?: string;
  servings: number;
  servingSize?: string;
  prepTime: number;
  cookTime: number;
  yield: number;
  yieldUnit: string;
  difficulty: 'easy' | 'medium' | 'hard';
  ingredients: GeneratedIngredient[];
  instructions: string[];
  allergens: string[];
  dietaryFlags: string[];
}

export type StreamCallback = (chunk: string) => void;
export type CompleteCallback = (recipe: GeneratedRecipe) => void;
export type ErrorCallback = (error: Error) => void;

interface SSEMessage {
  chunk?: string;
  done?: boolean;
  error?: string;
}

function parseSSEMessage(data: string): SSEMessage | null {
  try {
    return JSON.parse(data) as SSEMessage;
  } catch {
    return null;
  }
}

function processSSELine(
  line: string,
  state: { fullResponse: string },
  onChunk: StreamCallback
): SSEMessage | null {
  if (!line.startsWith('data: ')) {
    return null;
  }

  const data = line.slice(6);
  const parsed = parseSSEMessage(data);

  if (!parsed) {
    return null;
  }

  if (parsed.error) {
    throw new GenerateRecipeError(500, parsed.error);
  }

  if (parsed.chunk) {
    state.fullResponse += parsed.chunk;
    onChunk(parsed.chunk);
  }

  return parsed;
}

function processSSEBuffer(
  buffer: string,
  state: { fullResponse: string },
  onChunk: StreamCallback
): {
    remaining: string;
    isDone: boolean 
  } {
  const lines = buffer.split('\n');
  const remaining = lines.pop() ?? '';

  for (const line of lines) {
    const message = processSSELine(line, state, onChunk);
    if (message?.done) {
      return {
        remaining,
        isDone: true 
      };
    }
  }

  return {
    remaining,
    isDone: false 
  };
}

/**
 * Generate a recipe using Bedrock AI with streaming response.
 * Uses Server-Sent Events for real-time streaming.
 */
export async function generateRecipeStream(
  request: GenerateRecipeRequest,
  onChunk: StreamCallback,
  onComplete: CompleteCallback,
  onError: ErrorCallback,
  signal?: AbortSignal
): Promise<void> {
  const url = `${API_BASE_URL}/v1/recipes/generate-stream`;

  try {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Accept': 'text/event-stream',
      },
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      throw new GenerateRecipeError(
        response.status,
        `Failed to generate recipe: ${response.statusText}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new GenerateRecipeError(500, 'No response body');
    }

    const decoder = new TextDecoder();
    const state = { fullResponse: '' };
    const bufferState = { value: '' };

    while (true) {
      const readResult = await reader.read();

      if (readResult.done) break;

      bufferState.value += decoder.decode(readResult.value, { stream: true });
      const processResult = processSSEBuffer(bufferState.value, state, onChunk);
      bufferState.value = processResult.remaining;

      if (processResult.isDone) {
        const recipe = parseRecipeJson(state.fullResponse);
        onComplete(recipe);
        return;
      }
    }

    // If we get here without a done signal, try to parse what we have
    if (state.fullResponse) {
      const recipe = parseRecipeJson(state.fullResponse);
      onComplete(recipe);
    }
  } catch (error) {
    handleStreamError(error, onError);
  }
}

function handleStreamError(error: unknown, onError: ErrorCallback): void {
  if (error instanceof GenerateRecipeError) {
    onError(error);
    return;
  }

  if (error instanceof Error) {
    const errorToReport = error.name === 'AbortError'
      ? new GenerateRecipeError(0, 'Request cancelled')
      : new GenerateRecipeError(500, error.message);
    onError(errorToReport);
    return;
  }

  onError(new GenerateRecipeError(500, 'Unknown error'));
}

/**
 * Generate a recipe using Bedrock AI (non-streaming fallback).
 */
export async function generateRecipe(
  request: GenerateRecipeRequest
): Promise<GeneratedRecipe> {
  const url = `${API_BASE_URL}/v1/recipes/generate`;

  const authHeaders = await getAuthHeaders();
  const response = await fetch(url, {
    method: 'POST',
    headers: authHeaders,
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new GenerateRecipeError(
      response.status,
      `Failed to generate recipe: ${response.statusText}`
    );
  }

  const data = await response.json() as { recipe: string };
  return parseRecipeJson(data.recipe);
}

function parseRecipeJson(jsonString: string): GeneratedRecipe {
  const cleaned = cleanJsonString(jsonString);

  try {
    return JSON.parse(cleaned) as GeneratedRecipe;
  } catch {
    throw new GenerateRecipeError(500, 'Failed to parse recipe JSON from AI response');
  }
}

function cleanJsonString(jsonString: string): string {
  const trimmed = jsonString.trim();

  const withoutJsonPrefix = trimmed.startsWith('```json')
    ? trimmed.slice(7)
    : trimmed;

  const withoutPrefix = withoutJsonPrefix.startsWith('```')
    ? withoutJsonPrefix.slice(3)
    : withoutJsonPrefix;

  const withoutSuffix = withoutPrefix.endsWith('```')
    ? withoutPrefix.slice(0, -3)
    : withoutPrefix;

  return withoutSuffix.trim();
}

export class GenerateRecipeError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string
  ) {
    super(message);
    this.name = 'GenerateRecipeError';
  }
}
